// CrossSectionModal.jsx
import React, { useRef, useEffect, useState } from "react";
import { fitCircleKasa } from "../../../utils/bestFitDiameterUtils";
import { anatomyConstants } from "../../../constants/anatomyConstants";

const CrossSectionModal = ({
  crossSectionPoints, // array of THREE.Vector3 with x and y in plane space
  width = 200,
  height = 200,
  isMobile,
}) => {
  const canvasRef = useRef(null);
  const [fittedCircle, setFittedCircle] = useState(null);

  // Helper: Convert THREE.Vector3 objects to plain {x,y} objects.
  const convertPoints = (pts) => pts.map((pt) => ({ x: pt.x, y: pt.y }));

  // Compute best-fit circle using least-squares (Kasa) method.
  useEffect(() => {
    if (crossSectionPoints && crossSectionPoints.length > 0) {
      const pts2D = convertPoints(crossSectionPoints);
      const circle = fitCircleKasa(pts2D);
      setFittedCircle(circle);
    } else {
      setFittedCircle(null);
    }
  }, [crossSectionPoints]);

  useEffect(() => {
    const ctx = canvasRef.current.getContext("2d");
    ctx.clearRect(0, 0, width, height);

    // Draw grid background.
    const gridSpacing = 20;
    ctx.strokeStyle = "#e0e0e0";
    for (let x = 0; x <= width; x += gridSpacing) {
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, height);
      ctx.stroke();
    }
    for (let y = 0; y <= height; y += gridSpacing) {
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(width, y);
      ctx.stroke();
    }

    // Define canvas center as origin.
    const offsetX = width / 2;
    const offsetY = height / 2;

    // Compute a scale factor based on the extents of your points.
    let scale = 1;
    if (crossSectionPoints && crossSectionPoints.length > 0) {
      let minX = Infinity,
        maxX = -Infinity,
        minY = Infinity,
        maxY = -Infinity;
      crossSectionPoints.forEach((pt) => {
        if (pt.x < minX) minX = pt.x;
        if (pt.x > maxX) maxX = pt.x;
        if (pt.y < minY) minY = pt.y;
        if (pt.y > maxY) maxY = pt.y;
      });
      const bbWidth = maxX - minX;
      const bbHeight = maxY - minY;
      scale = Math.min((width * 0.8) / bbWidth, (height * 0.8) / bbHeight);
    }

    // Draw cross-section points as red circles.
    ctx.fillStyle = "#ff0000";
    ctx.strokeStyle = "#000000";
    ctx.lineWidth = 1;
    crossSectionPoints.forEach((pt) => {
      const x = pt.x * scale + offsetX;
      const y = -pt.y * scale + offsetY; // Flip Y so that positive is up.
      ctx.beginPath();
      ctx.arc(x, y, 3, 0, 2 * Math.PI);
      ctx.fill();
      ctx.stroke();
    });

    // Draw best-fit circle (if computed) in green.
    if (fittedCircle) {
      const { center, radius } = fittedCircle;
      // Transform the circle's center and radius into canvas units.
      const canvasCenterX = center.x * scale + offsetX;
      const canvasCenterY = -center.y * scale + offsetY;
      const canvasRadius = radius * scale;
      ctx.strokeStyle = "#00aa00";
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(canvasCenterX, canvasCenterY, canvasRadius, 0, 2 * Math.PI);
      ctx.stroke();
    }
  }, [crossSectionPoints, fittedCircle, width, height]);

  // Check if mobile portrait (upside down) view
  const isMobilePortrait =
    isMobile &&
    typeof window !== "undefined" &&
    window.innerWidth < window.innerHeight;

  return (
    <div
      className={`fixed ${
        isMobilePortrait ? "top-20 right-20" : "top-20 left-20"
      } m-4 z-50`}
      style={{
        "--main-color": anatomyConstants.mainColor,
        "--main-color-bold": anatomyConstants.mainColorBold,
      }}
    >
      <div
        className="relative bg-white rounded-lg shadow-lg border border-gray-200 p-4"
        style={{
          backgroundColor: "rgba(255, 255, 255, 0.95)",
        }}
      >
        <h2 className="text-lg font-bold mb-2 text-[var(--main-color)] font-inter">
          Cross Section
        </h2>
        <canvas
          ref={canvasRef}
          width={width}
          height={height}
          className="border border-gray-300 rounded"
        />
        {fittedCircle && (
          <div className="mt-2 text-sm text-gray-700 font-inter">
            Diameter: {(fittedCircle.radius * 2).toFixed(2)} mm
          </div>
        )}
      </div>
    </div>
  );
};

export default CrossSectionModal;
